/**
 * AssignmentsTour-specific types and step definitions.
 *
 * The AssignmentsTour walks users through conversation assignments —
 * assigning to yourself or team members, filtering by assignee, and
 * understanding the Assigned to Me view.
 */

import type { TourStep } from '../types';

/** Unique identifiers for each AssignmentsTour step */
export type AssignmentsTourStepId =
  | 'assignments_welcome'
  | 'highlight_all_tab'
  | 'highlight_assign_btn'
  | 'explain_assign_menu'
  | 'highlight_assigned_agents'
  | 'highlight_assigned_tab'
  | 'assigned_view'
  | 'highlight_filter_btn'
  | 'highlight_filter_agents'
  | 'assignments_complete';

/** Ordered list of all AssignmentsTour steps */
export const ASSIGNMENTS_TOUR_STEPS: TourStep[] = [
  {
    id: 'assignments_welcome',
    type: 'modal',
    title: '',
    content: '',
  },
  {
    id: 'highlight_all_tab',
    type: 'highlight',
    targetSelector: '[data-tour-id="inbox-all"]',
    title: 'All Conversations',
    content:
      'This is your All Conversations view. Every conversation happening across your agency is visible here, giving your entire team full transparency so no client inquiry goes unnoticed.',
    placement: 'right',
  },
  {
    id: 'highlight_assign_btn',
    type: 'highlight',
    targetSelector: '[data-tour-id="assign-btn"]',
    title: 'Assign a Conversation',
    content:
      'Click "Assign" to see a list of your team members. You can assign the conversation to yourself or to another agent, placing it in their Assigned to Me inbox so they can take ownership.',
    placement: 'bottom',
    advanceOnElement: '[data-tour-id="assign-menu"]',
  },
  {
    id: 'explain_assign_menu',
    type: 'highlight',
    targetSelector: '[data-tour-id="assign-menu"]',
    title: 'Select a Team Member',
    content:
      'This dropdown shows everyone in your agency — including yourself at the top. Select any team member to assign the conversation to them. You can have multiple agents assigned to the same conversation for collaborative handling.',
    placement: 'left',
    allowInteraction: true,
    advanceOnElementRemoved: '[data-tour-id="assign-menu"]',
  },
  {
    id: 'highlight_assigned_agents',
    type: 'highlight',
    targetSelector: '[data-tour-id="assigned-agents"]',
    title: 'You\'re Now Assigned',
    content:
      'Your name now appears here, showing the entire team that you\'re responsible for this conversation. Anyone in your agency can see who is handling each conversation at a glance.',
    placement: 'bottom',
  },
  {
    id: 'highlight_assigned_tab',
    type: 'highlight',
    targetSelector: '[data-tour-id="inbox-assigned"]',
    title: 'Assigned to Me',
    content:
      'Now let\'s see your personal workspace. Click this tab to filter the conversation list to only conversations assigned to you.',
    placement: 'right',
    advanceOnNavigate: '/dashboard?view=assigned',
  },
  {
    id: 'assigned_view',
    type: 'highlight',
    targetSelector: '[data-tour-id="inbox-assigned"]',
    title: 'Your Assigned Conversations',
    content:
      'Here you can see all conversations assigned to you. This focused view helps you stay on top of your responsibilities without sifting through the full inbox.',
    placement: 'right',
  },
  {
    id: 'highlight_filter_btn',
    type: 'highlight',
    targetSelector: '[data-tour-id="filter-btn"]',
    title: 'Filter Conversations',
    content:
      'Click the Sort & Filter button to narrow down your conversation list. You can filter by assigned agent, tags, and more.',
    placement: 'right',
    advanceOnElement: '[data-tour-id="filter-dropdown"]',
  },
  {
    id: 'highlight_filter_agents',
    type: 'highlight',
    targetSelector: '[data-tour-id="filter-assigned-section"]',
    title: 'Filter by Agent',
    content:
      'Under "Assigned To," you can filter conversations by specific team members. This is a powerful way to check on an agent\'s workload, help tag-team conversations, or make sure nothing falls through the cracks.',
    placement: 'right',
    allowInteraction: true,
    advanceOnElementRemoved: '[data-tour-id="filter-dropdown"]',
  },
  {
    id: 'assignments_complete',
    type: 'modal',
    title: 'Assignments Tour Complete',
    content:
      'You now know how to assign conversations and filter them across your agency.',
  },
];
